import { useEffect, useMemo, useState } from "react";
import { listNotifications, markNotificationRead, markAllNotificationsRead } from "@/lib/api";
import { Mail, Users, Zap, Calendar, Send, Clock, CheckCheck, AlertCircle, AtSign } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { NotificationDetailModal } from "@/components/admin/NotificationDetailModal";

const KIND_META = {
  contact_message:    { label: "Message",         icon: Mail,     color: "text-blue-700 bg-blue-50 border-blue-200" },
  newsletter_signup:  { label: "Signup",          icon: Users,    color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  action_taken:       { label: "Action",          icon: Zap,      color: "text-amber-700 bg-amber-50 border-amber-200" },
  event_rsvp:         { label: "RSVP",            icon: Calendar, color: "text-purple-700 bg-purple-50 border-purple-200" },
  priest_outreach:    { label: "Parish Toolkit",  icon: Send,     color: "text-ccc-plum bg-ccc-gold/10 border-ccc-gold/30" },
  task_assigned:      { label: "Task",            icon: Clock,    color: "text-stone-700 bg-stone-100 border-stone-300" },
  task_overdue:       { label: "Overdue",         icon: AlertCircle, color: "text-red-700 bg-red-50 border-red-200" },
  mention:            { label: "Mention",         icon: AtSign,   color: "text-ccc-purple bg-ccc-purple/10 border-ccc-purple/30" },
};

export default function Inbox() {
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState({ only_unread: false, kind: "" });
  const [detailNotif, setDetailNotif] = useState(null);

  const load = () => listNotifications(filter).then(r => {
    setItems(r.items || []);
    setUnreadCount(r.unread_count || 0);
  });
  useEffect(() => { load(); }, [filter]);  // eslint-disable-line

  const markAll = async () => {
    await markAllNotificationsRead();
    toast.success("All notifications marked as read");
    load();
  };
  const markOne = async (id) => {
    await markNotificationRead(id);
    load();
  };
  const openDetail = async (n) => {
    setDetailNotif(n);
    if (!n.read_by || !n.read_by.length) {
      await markNotificationRead(n.id);
      load();
    }
  };

  const kinds = useMemo(() => Object.keys(KIND_META), []);

  return (
    <div data-testid="admin-inbox-page">
      <div className="flex justify-between items-end mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-ccc-gold">Everything incoming</div>
          <h1 className="font-serif text-4xl text-ccc-plum">Inbox</h1>
          <div className="text-sm text-stone-600 mt-1">
            Messages, signups, actions, RSVPs, parish toolkit sends — one feed. {unreadCount > 0 && <strong className="text-ccc-purple">{unreadCount} unread.</strong>}
          </div>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAll}
            data-testid="inbox-mark-all-read"
            className="inline-flex items-center gap-2 border border-ccc-purple text-ccc-purple hover:bg-ccc-purple hover:text-white px-4 py-2 text-sm uppercase tracking-wider font-semibold"
          >
            <CheckCheck size={14}/> Mark all read
          </button>
        )}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={filter.only_unread}
            onChange={e => setFilter({ ...filter, only_unread: e.target.checked })}
            data-testid="inbox-unread-filter"
            className="accent-ccc-purple"
          />
          Only unread
        </label>
        <select
          value={filter.kind}
          onChange={e => setFilter({ ...filter, kind: e.target.value })}
          data-testid="inbox-kind-filter"
          className="px-3 py-1.5 border border-stone-300 bg-white text-sm focus:outline-none focus:border-ccc-purple"
        >
          <option value="">All types</option>
          {kinds.map(k => <option key={k} value={k}>{KIND_META[k].label}</option>)}
        </select>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 text-stone-500" data-testid="inbox-empty">
          <div className="text-4xl mb-2">☑</div>
          <div className="font-serif text-2xl text-ccc-plum mb-1">All caught up.</div>
          <div className="text-sm">No notifications match this filter.</div>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="inbox-list">
          {items.map(n => (
            <InboxRow
              key={n.id}
              n={n}
              onMarkRead={() => markOne(n.id)}
              onOpen={() => openDetail(n)}
            />
          ))}
        </ul>
      )}

      {detailNotif && (
        <NotificationDetailModal
          notification={detailNotif}
          onClose={() => { setDetailNotif(null); load(); }}
          onUpdated={load}
        />
      )}
    </div>
  );
}

function InboxRow({ n, onMarkRead, onOpen }) {
  const meta = KIND_META[n.kind] || { label: n.kind, icon: Mail, color: "text-stone-600 bg-stone-50 border-stone-200" };
  const Icon = meta.icon;
  const isUnread = !n.read_by || !n.read_by.length;
  const commentCount = (n.comments || []).length;
  let when = n.created_at;
  try { when = formatDistanceToNow(new Date(n.created_at), { addSuffix: true }); } catch { /* ignore */ }
  return (
    <li
      className={`flex items-start gap-3 border p-3 cursor-pointer hover:border-ccc-purple ${isUnread ? "bg-white border-ccc-purple/20 shadow-sm" : "bg-stone-50 border-stone-200"}`}
      data-testid={`inbox-row-${n.id}`}
      onClick={onOpen}
    >
      <div className={`w-9 h-9 flex items-center justify-center border ${meta.color}`}>
        <Icon size={16}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${meta.color}`}>{meta.label}</span>
          {isUnread && <span className="w-2 h-2 rounded-full bg-ccc-purple" aria-label="Unread"/>}
          {commentCount > 0 && (
            <span className="text-[10px] uppercase tracking-wider text-stone-500" data-testid={`comment-count-${n.id}`}>
              💬 {commentCount}
            </span>
          )}
          <span className="text-xs text-stone-500 ml-auto">{when}</span>
        </div>
        <div className="font-medium text-ccc-plum mt-1">{n.title}</div>
        {n.subtitle && <div className="text-xs text-stone-600 mt-0.5">{n.subtitle}</div>}
      </div>
      {isUnread && (
        <button
          onClick={(e) => { e.stopPropagation(); onMarkRead(); }}
          data-testid={`inbox-mark-read-${n.id}`}
          className="text-xs text-ccc-purple hover:text-ccc-purple-dark border border-ccc-purple/30 hover:border-ccc-purple px-2 py-1 shrink-0"
        >
          Mark read
        </button>
      )}
    </li>
  );
}
